import { Button, Input, Tab, Tabs } from '@nextui-org/react'
import HomeSendReceiveCard from './SendReceiveCard'
import { useEffect, useMemo, useRef, useState } from 'react'
import useAllCoinHooks, { CoinType } from '@/hooks/useAllCoinHooks'
import useSWR from 'swr'
import { serverOrderCreate, serverOrderPrice } from '@/api/order'
import toast from 'react-hot-toast'
import { useWeb3Modal, useWeb3ModalAccount } from '@web3modal/ethers/react'
import { Link, useNavigate } from 'react-router-dom'
import Paste from './Paste'
import Close from './Close'
import codeLength from '@/utils/codeLength'
import Difference from './Modal/Difference'
import { useTranslation } from 'react-i18next'

interface IState {
	recv: CoinType | undefined
	send: CoinType | undefined
}

export type IType = 'fixed' | 'float'

const StateInit: IState = {
	recv: undefined,
	send: undefined,
}

export const valueInit = '0'

export default function HomeSwap() {
	const { t } = useTranslation()
	const DifferenceRef = useRef<any>(null)

	const { isConnected } = useWeb3ModalAccount()
	const { open } = useWeb3Modal()

	const navigate = useNavigate()

	const { list } = useAllCoinHooks().data

	const [state, setState] = useState<IState>(StateInit)
	const [stateValue, setStateValue] = useState<{
		send: string
		recv: string
	}>({ send: valueInit, recv: valueInit })
	const [stateMinMax, setStateMinMax] = useState<{
		min: string
		max: string
	}>({
		min: '0',
		max: '0',
	})

	const [type, setType] = useState<IType>('fixed')
	const [isSelectType, setIsSelectType] = useState<'send' | 'recv' | ''>('')

	// 接受地址
	const [recvAddress, setRecvAddress] = useState('')
	// 兑换loading
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (list.length > 0) {
			setState({
				recv: list.find(ite => ite.recv === '1' && ite.coin === 'USDT'),
				send: list.find(ite => ite.send === '1'),
			})
			setStateValue({
				...stateValue,
				send: '0.1',
			})
			setIsSelectType('send')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [list])

	const { data, ...dataPriceArgs } = useSWR(
		isSelectType
			? isSelectType === 'send'
				? [type, stateValue.send, state.send?.code, state.recv?.code]
				: [type, stateValue.recv, state.recv?.code, state.send?.code]
			: null,
		async ([type, value, code, otherCode]) => {
			console.log('type, value, code, otherCode', type, value, code, otherCode)
			if (!code || !otherCode) return
			return await serverOrderPrice({
				type: type,
				from: code,
				to: otherCode,
				amount: Number(value),
			}).then(res => {
				return res.data
			})
		},
		{
			revalidateIfStale: false,
			revalidateOnFocus: false,
		},
	)

	const dataInfo = useMemo(() => {
		if (data) {
			if (isSelectType === 'send') {
				setStateValue({
					send: data?.from.amount.toString(),
					recv: data?.to.amount.toString(),
				})
				setStateMinMax({
					min: data?.from.min,
					max: data?.from.max,
				})
				return {
					fromRate: data?.from.rate,
					toRate: data?.to.rate,
				}
			} else {
				setStateValue({
					send: data?.to.amount.toString(),
					recv: data?.from.amount.toString(),
				})
				return {
					fromRate: data?.to.rate,
					toRate: data?.from.rate,
				}
			}
		}
		return {}
	}, [data, isSelectType])

	// 兑换提交
	const submit = async () => {
		setLoading(true)
		if (!isConnected) {
			toast.error(t('swap.tips.no.wallet'))
			open()
			setLoading(false)
			return
		}
		if (!stateValue.send) {
			toast.error(t('swap.tips.no.number'))
			setLoading(false)
			return
		}
		if (recvAddress === '') {
			toast.error(t('swap.tips.no.address'), { id: 'swapRecvAddressNo' })
			setLoading(false)
			return
		}
		const s = codeLength((state.recv?.code || '') as string)
		console.log('s', s, 'recvAddress.length', state.recv?.code, recvAddress.length, s !== recvAddress.length)
		if (s !== recvAddress.length) {
			toast.error(t('swap.tips.address.error'), { id: 'swapRecvAddressNo' })
			setLoading(false)
			return
		}
		try {
			const dataParams: any = await serverOrderCreate({
				type,
				from: state.send?.code || '',
				to: state.recv?.code || '',
				amount: Number(stateValue.send),
				to_address: recvAddress,
			}).finally(() => setLoading(false))
			if (dataParams.code === 0) {
				navigate(`/ordersDetails?id=${dataParams.data.id}`)
			} else {
				// toast.error(dataParams.msg || t('swap.tips.error'), { id: 'exchangeError' })
			}
		} catch (error) {
			setLoading(false)
		}
	}

	const isOpacitySend = useMemo(() => {
		if (!data) return false
		return data?.from?.max && Number(data?.from?.max) < Number(stateValue.send)
			? true
			: data?.from?.min && Number(data?.from?.min) > Number(stateValue.send)
				? true
				: false
	}, [data, stateValue.send])

	return (
		<div className="tail-home-swap">
			<div className="flex w-full flex-col items-center justify-center px-3 py-4">
				<div className="relative flex w-full flex-col gap-4">
					<div className="relative">
						<div className="tail-send-from z-[3] !pb-[2rem]">
							<HomeSendReceiveCard
								isSend
								otherCoin={state.recv?.coin || ''}
								selectInfo={state.send}
								color={state.send?.color || '#f7931a'}
								onSelect={item => setState(prev => ({ ...prev, send: item }))}
								value={stateValue.send}
								onValueChange={value => {
									setStateValue(prev => ({ ...prev, send: value }))
									setIsSelectType('send')
								}}
								min={Number(stateMinMax.min) || 0}
								max={Number(stateMinMax.max) || 0}
								otherCode={state.recv?.code || ''}
								type={type}
								otherRate={dataInfo.fromRate}
								isPriceLoading={dataPriceArgs.isLoading || dataPriceArgs.isValidating}
								isOpacity={isOpacitySend}
								isOpacityText={
									data?.from?.max && Number(data?.from?.max) < Number(stateValue.send)
										? t('swap.tips.max', { msg: data?.from.max })
										: data?.from?.min && Number(data?.from?.min) > Number(stateValue.send)
											? t('swap.tips.min', { msg: data?.from.min })
											: ''
								}
							/>
						</div>
					</div>
					<div className="tail-send-to">
						<HomeSendReceiveCard
							otherCoin={state.send?.coin || ''}
							selectInfo={state.recv}
							color={state.recv?.color || '#40A2FF'}
							onSelect={item => setState(prev => ({ ...prev, recv: item }))}
							value={stateValue.recv}
							onValueChange={value => {
								setStateValue(prev => ({ ...prev, recv: value }))
								setIsSelectType('recv')
							}}
							otherCode={state.send?.code || ''}
							type={type}
							otherRate={dataInfo.toRate}
							isPriceLoading={dataPriceArgs.isLoading || dataPriceArgs.isValidating}
						/>
					</div>
					<div
						onClick={() => {
							const { send, recv } = state
							if (send && recv) {
								setState({
									send: recv,
									recv: send,
								})
							}
						}}
						className="absolute left-0 top-1/2 z-10 flex w-full cursor-pointer items-center justify-center"
					>
						<svg className="" width="46" height="47" viewBox="0 0 46 47" fill="none" xmlns="http://www.w3.org/2000/svg">
							<rect
								x="44.25"
								y="2.16797"
								width="42.7222"
								height="42"
								rx="21"
								transform="rotate(90 44.25 2.16797)"
								fill="url(#paint0_linear_20_3559)"
							/>
							<rect
								x="44.25"
								y="2.16797"
								width="42.7222"
								height="42"
								rx="21"
								transform="rotate(90 44.25 2.16797)"
								stroke="black"
								strokeWidth="3"
							/>
							<path
								d="M26.1389 15.2235L26.1389 31.8346L31.1945 26.7791"
								stroke="white"
								strokeWidth="1.44444"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M20.3611 31.8346L20.3611 15.2235L15.3055 20.279"
								stroke="white"
								strokeWidth="1.44444"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<defs>
								<linearGradient
									id="paint0_linear_20_3559"
									x1="62.5342"
									y1="3.66797"
									x2="62.5342"
									y2="42.668"
									gradientUnits="userSpaceOnUse"
								>
									<stop stopColor="#192079" />
									<stop offset="1" stopColor="#1B52EB" />
								</linearGradient>
							</defs>
						</svg>
					</div>
				</div>
				<div className="mt-5 flex w-full items-center gap-4">
					<div className="shrink-0 text-xs text-white">{t('swap.btn.title')}</div>
					<Input
						variant="bordered"
						classNames={{
							input: 'text-xs',
							inputWrapper: 'h-11 pr-0 rounded-[2rem] bg-[#0A0D11] !border-[#202020]',
						}}
						value={recvAddress}
						onValueChange={setRecvAddress}
						endContent={
							<>
								{recvAddress.length === 0 ? (
									<div className="m-[1px] flex h-[calc(100%_-_2px)] items-center justify-center rounded-full border border-white/[0.04] bg-white/[0.04] px-3">
										<Paste onChange={setRecvAddress} />
									</div>
								) : (
									<Close onClick={() => setRecvAddress('')} />
								)}
							</>
						}
						placeholder={t('swap.input.tips')}
					/>
				</div>
				<div className="mb-6 mt-5 flex w-full flex-col items-start gap-2">
					<div className="flex flex-col items-start">
						<div className="text-sm text-white">{t('swap.title.order')}</div>
						<div
							onClick={() => {
								DifferenceRef.current?.onOpen()
							}}
							className="flex w-full cursor-pointer items-center gap-1 py-2"
						>
							<svg className="shrink-0" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M3.81104 3.93045C3.8822 3.71116 4.01256 3.51601 4.18783 3.36623C4.36309 3.21645 4.57658 3.11802 4.80428 3.0819C5.03198 3.04577 5.26509 3.07328 5.47811 3.16146C5.69113 3.24964 5.8756 3.39509 6.01115 3.58158C6.14669 3.76808 6.22787 3.98835 6.24599 4.21818C6.26411 4.44801 6.21827 4.67844 6.11362 4.88387C6.00897 5.0893 5.84974 5.26165 5.65317 5.38212C5.4566 5.50259 5.23055 5.56635 5 5.56635V5.98321M5 8.89978C2.92893 8.89978 1.25 7.22085 1.25 5.14978C1.25 3.07871 2.92893 1.39978 5 1.39978C7.07107 1.39978 8.75 3.07871 8.75 5.14978C8.75 7.22085 7.07107 8.89978 5 8.89978ZM5.02075 7.23311V7.27478L4.97925 7.27486V7.23311H5.02075Z"
									stroke="#747474"
									stroke-width="0.833333"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>

							<span className="whitespace-nowrap text-[0.5rem] text-[#747474]">{t('swap.btn.tipss')}</span>
						</div>
					</div>
					<div className="flex w-full items-center justify-center">
						<Tabs
							classNames={{
								tabList: 'w-full p-0 h-10 rounded-full bg-transparent border !border-[#202020]',
								tab: 'h-[calc(100%_-_2px)]  rounded-md  text-xs',
								cursor: 'rounded-full mx-[1px]  !bg-transparent border border-[#40A2FF]',
								tabContent: 'text-white',
							}}
							aria-label="Tabs variants"
							selectedKey={type}
							onSelectionChange={key => setType(key as any)}
						>
							<Tab key="fixed" title={t('swap.tab.1')} />
							<Tab key="float" title={t('swap.tab.2')} />
						</Tabs>
					</div>
				</div>

				<Button
					isDisabled={isOpacitySend}
					onClick={submit}
					isLoading={loading}
					className="tail-btn-active h-10 w-full rounded-md disabled:bg-[#d9d9d9] disabled:text-[#818181] disabled:opacity-100"
				>
					<span className="text-base font-bold">{t('swap.btn.swap')}</span>
					<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M7.33335 5.73313L9.08335 7.48313M9.08335 7.48313L7.33335 9.23313M9.08335 7.48313H0.916687M2.66669 4.56647L0.916687 2.81647M0.916687 2.81647L2.66669 1.06647M0.916687 2.81647H9.08335"
							stroke="white"
							strokeWidth="1.16667"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</Button>
			</div>

			<div className="px-6 pb-5 text-center text-[0.625rem] text-[#D9D9D9]">
				{t('swap.tips.1')}{' '}
				<Link to="/serve" className="text-[#8F9FFF]">
					{t('swap.tips.2')}
				</Link>
				{t('swap.tips.3')}{' '}
				<Link to="/privacy" className="text-[#8F9FFF]">
					{t('swap.tips.4')}
				</Link>
				{t('swap.tips.5')}
			</div>
			<Difference ref={DifferenceRef} />
		</div>
	)
}
